import { useMemo, useState } from 'react';

import { viewApi } from '@/api/services/viewApi';
import { Button, DropDownList, EmptyState } from '@/components/ui';
import type { ChinaAppSysView } from '@/types/entities';
import { entityTypeDisplayNames, type EntityTypes } from '@/types/enums';

import { AdminViewForm } from './forms/AdminViewForm';
import { useAdminList } from './lists/adminList';
import { MasterDetail, type MasterItem } from './MasterDetail';

/** Views — Port von `IwmAdminView.razor` (REQ-29 §2). */
const load = () => viewApi.get();

/**
 * Der Filter trägt einen Wert, der in `EntityTypes` nicht vorkommt; die Entität selbst
 * steht am Eintrag, damit sie ohne Typzusicherung zurückkommt.
 */
interface EntityOption {
  value: number;
  text: string;
  entity: EntityTypes | null;
}

const ALL_ENTITIES: EntityOption = { value: -1, text: 'Alle Entitäten', entity: null };
const ENTITY_FIELDS = { value: 'value', text: 'text' } as const;

export function AdminViews() {
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [entity, setEntity] = useState<EntityTypes | null>(null);
  const { rows, loading, error, reload } = useAdminList<ChinaAppSysView>(load);

  // Angeboten werden die Entitäten, zu denen es Views gibt — ein Eintrag ohne Treffer wäre
  // ein Filter ins Leere. Die gewählte bleibt dabei, auch wenn ihre letzte View wegfällt.
  const entityOptions = useMemo<EntityOption[]>(() => {
    const present = new Set<EntityTypes>(rows.map((view) => view.Entity));
    if (entity !== null) present.add(entity);
    return [
      ALL_ENTITIES,
      ...[...present]
        .map((value) => ({ value, text: entityTypeDisplayNames[value], entity: value }))
        .sort((left, right) => left.text.localeCompare(right.text, 'de-DE')),
    ];
  }, [entity, rows]);

  const items = useMemo<MasterItem[]>(
    () =>
      rows
        .filter((view) => entity === null || view.Entity === entity)
        .map((view) => ({
          id: view.Id,
          label: view.ViewName ?? '',
          searchText: `${view.ViewName ?? ''} ${entityTypeDisplayNames[view.Entity]}`,
          description: entityTypeDisplayNames[view.Entity],
        })),
    [entity, rows],
  );

  /**
   * `useAdminForm` meldet die Id des gespeicherten Datensatzes — und `0`, wenn gelöscht
   * wurde oder das Anlegen misslang. Dann steht keine View zur Anzeige.
   */
  const onSave = async (id: number) => {
    await reload();
    setSelectedId(id > 0 ? id : null);
  };

  return (
    <MasterDetail
      listLabel="Views"
      items={items}
      selectedId={selectedId}
      onSelect={setSelectedId}
      loading={loading}
      error={error}
      onRetry={() => {
        void reload();
      }}
      searchPlaceholder="Name der View"
      emptyText="Keine Views"
      filter={
        <DropDownList<number, EntityOption>
          value={entity ?? ALL_ENTITIES.value}
          onChange={(_value, item) => {
            setEntity(item?.entity ?? null);
          }}
          items={entityOptions}
          fields={ENTITY_FIELDS}
          label="Entität"
        />
      }
      toolbar={
        <Button
          content="New"
          iconClass="fa-solid fa-plus"
          onClick={() => {
            setSelectedId(0);
          }}
        />
      }
    >
      {selectedId === null ? (
        <EmptyState
          iconClass="fa-solid fa-table"
          title="Keine View gewählt"
          description="Wählen Sie links eine View aus oder legen Sie eine neue an."
        />
      ) : (
        <AdminViewForm id={selectedId} onSave={onSave} />
      )}
    </MasterDetail>
  );
}

export default AdminViews;
